import { Button } from "@/components/ui/button";
import { ArrowRight, Users2, MessagesSquare, Radio, Newspaper, Building2, Video, ShieldCheck } from "lucide-react";
import communityTradingRoom from "@/assets/community-trading-room.jpg";
import communitySignals from "@/assets/community-signals.jpg";
import communityDiscussion from "@/assets/community-discussion.jpg";

const features = [
  {
    icon: MessagesSquare,
    title: "Topic-Based Trading Rooms",
    description: "Specialized rooms for FX, commodities, indices and crypto — discuss setups with serious traders.",
  },
  {
    icon: ShieldCheck,
    title: "Verified Signal Rooms",
    description: "Signal channels with built-in accuracy tracking and credibility scoring — no more blind follows.",
  },
  {
    icon: Video,
    title: "Live Trading Sessions",
    description: "Watch top traders execute in real time — entries, exits, and reasoning shared on the spot.",
  },
  {
    icon: Newspaper,
    title: "Curated Market News",
    description: "Serious forex and macro news streamed directly into the community, filtered for impact.",
  },
  {
    icon: Building2,
    title: "Top Brokers Intel",
    description: "Up-to-date information on top-tier brokers — spreads, conditions, regulation and trader reviews.",
  },
  {
    icon: Users2,
    title: "Pro Trader Profiles",
    description: "Build a public, performance-backed trader identity that stands out in the community.",
  },
];

interface CommunitySectionProps {
  onCommunityCTA: () => void;
}

export const CommunitySection = ({ onCommunityCTA }: CommunitySectionProps) => {
  return (
    <section id="community-center" className="container mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 via-card to-primary/5 ring-1 ring-emerald-200/60 shadow-card">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 p-8 lg:p-14">
          {/* Left: Visual collage */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Main image */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-elevated ring-1 ring-emerald-200/60">
                <img
                  src={communityTradingRoom}
                  alt="Professional traders collaborating in a live trading room"
                  width={1024}
                  height={768}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 via-transparent to-transparent" />
              </div>

              {/* Floating signals card */}
              <div className="absolute -top-4 -right-4 w-44 rounded-xl overflow-hidden shadow-elevated ring-2 ring-card rotate-3">
                <img
                  src={communitySignals}
                  alt="Verified trading signals dashboard"
                  width={1024}
                  height={768}
                  loading="lazy"
                  className="w-full h-28 object-cover"
                />
                <div className="bg-card px-3 py-2 flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-[10px] font-bold text-foreground">Verified Signals</span>
                </div>
              </div>

              {/* Floating discussion card */}
              <div className="absolute -bottom-4 -left-4 w-44 rounded-xl overflow-hidden shadow-elevated ring-2 ring-card -rotate-3">
                <img
                  src={communityDiscussion}
                  alt="Live trader discussion room"
                  width={1024}
                  height={768}
                  loading="lazy"
                  className="w-full h-28 object-cover"
                />
                <div className="bg-card px-3 py-2 flex items-center gap-1.5">
                  <Radio className="h-3.5 w-3.5 text-primary animate-pulse" />
                  <span className="text-[10px] font-bold text-foreground">Live Now · 248</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col justify-center">
            <span className="inline-flex items-center gap-1.5 self-start text-[11px] font-bold tracking-widest text-emerald-700 uppercase mb-3 bg-emerald-500/10 px-3 py-1 rounded-full">
              <Users2 className="h-3 w-3" />
              Community Center
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
              A serious{" "}
              <span className="bg-gradient-to-r from-emerald-600 to-emerald-800 bg-clip-text text-transparent">
                social network
              </span>{" "}
              built only for traders.
            </h2>
            <p className="mt-4 text-base text-foreground/70 leading-relaxed">
              JouRex Community Center is a dedicated social network for traders — build your professional
              profile, join specialized rooms for every market, follow verified signal channels, watch live
              trading sessions, and stay on top of serious market news and top broker intel. No noise,
              just traders.
            </p>

            {/* Features grid */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((f) => (
                <div key={f.title} className="flex items-start gap-2.5">
                  <div className="shrink-0 h-9 w-9 rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20 flex items-center justify-center">
                    <f.icon className="h-4 w-4 text-emerald-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground">{f.title}</div>
                    <div className="text-xs text-foreground/60 leading-snug">{f.description}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                onClick={onCommunityCTA}
                className="h-12 px-6 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-700 hover:to-emerald-900 text-white font-semibold shadow-elevated"
              >
                JouRex Community Center
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <span className="text-xs text-foreground/50">
                Free to join · Trader-only access
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
